// =============================================================================
// VandieZ shipper-web -- Post a Load page (/loads/new)
// =============================================================================
// MVP form mirroring the mobile-shipper "Post a Load" flow but flat — every
// required field on a single scrollable form. Latitudes / longitudes are
// hand-typed for the MVP (a real map picker lands later alongside the lat/
// lng lookup service the mobile clients use).
//
// Pre-submit invariants enforced client-side (so the form doesn't even
// reach the backend with garbage):
//   * weight_kg <= required_payload_kg
//   * budget_min <= budget_max
//   * pickup_window_end > pickup_window_start
// =============================================================================

import { AlertCircle, ArrowLeft, Loader2, Plus, Send, Trash2 } from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  loadsApi,
  pricingModelsApi,
  referenceDataApi,
  routeApi,
} from "@/api/client";
import { PageHeader } from "@/components/AppShell";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import { ApiError } from "@/types/auth";
import type {
  HamaliRequired,
  Load,
  LoadPostPayload,
  LoadStopPostPayload,
  PricingModelCode,
  PublicPricingModel,
  ReferenceItem,
  VehicleType,
} from "@/types/load";

const VEHICLE_TYPES: { value: VehicleType; label: string; payload: number }[] = [
  { value: "tata_ace", label: "Tata Ace (Chhota Hathi)", payload: 750 },
  { value: "pickup_8ft", label: "Pickup 8ft", payload: 1500 },
  { value: "tata_407", label: "Tata 407", payload: 2500 },
  { value: "lcv", label: "LCV (Light Commercial)", payload: 4000 },
];

// C-24. Fallback only. The real list is `reference_data` category
// `goods_type`, fetched below -- curating a goods type in the admin screen
// has to reach this form with no client release, which a hardcoded list
// would defeat. This array exists so the form still works if the fetch
// fails; `other` is always in the catalog, so it can never offer something
// the server would not recognise.
const MATERIAL_FALLBACK: ReferenceItem[] = [
  { code: "other", label: "Other", sort_order: 999 },
];

const HAMALI_OPTIONS: { value: HamaliRequired; label: string }[] = [
  { value: "self_arrange", label: "I'll arrange loading/unloading myself" },
  { value: "platform_arrange", label: "Platform arranges labourers" },
  { value: "none", label: "Not required" },
];

// Each additional drop the shipper adds beyond the main one. Same
// shape as the main drop minus window/material — those default to NULL
// per-stop and inherit the load-level material at read time.
interface ExtraDropDraft {
  address: string;
  instructions: string;
  contact_name: string;
  contact_phone: string;
  lat: string;
  lng: string;
  district: string;
}

function emptyExtraDrop(): ExtraDropDraft {
  return {
    address: "",
    instructions: "",
    contact_name: "",
    contact_phone: "",
    lat: "",
    lng: "",
    district: "",
  };
}

interface FormState {
  pickup_address: string;
  pickup_contact_name: string;
  pickup_contact_phone: string;
  pickup_lat: string;
  pickup_lng: string;
  pickup_district: string;
  pickup_window_start: string;
  pickup_window_end: string;

  drop_address: string;
  drop_contact_name: string;
  drop_contact_phone: string;
  drop_lat: string;
  drop_lng: string;
  drop_district: string;

  material_category: string;
  material_description: string;
  weight_kg: string;
  // C-23. Strings like every other numeric field here -- the form holds raw
  // input and converts once at submit, so a half-typed "8." is not a NaN.
  cargo_length_ft: string;
  cargo_width_ft: string;
  cargo_height_ft: string;
  required_vehicle_type: VehicleType;
  required_payload_kg: string;
  hamali_required: HamaliRequired;

  pricing_model: PricingModelCode;
  fixed_price_inr: string;
  budget_min: string;
  budget_max: string;
  bid_window_hours: string;
}

function toIsoOrEmpty(localDt: string): string {
  // <input type="datetime-local"> emits 'YYYY-MM-DDTHH:MM'; backend wants
  // a full ISO string with timezone. Treat the typed value as IST since
  // shippers post in local time.
  if (localDt.length === 0) {return "";}
  // Append IST offset; Date.parse handles it.
  return new Date(`${localDt}:00+05:30`).toISOString();
}

// If the user landed here via "Repost" on My Loads, we get the source
// Load attached in router state. Pre-fill the form so the only thing
// they need to update is the pickup window (since the old one is in
// the past) and anything else they want to tweak.
function fromRepost(load: Load, defaultVehicle: VehicleType): FormState {
  return {
    // A repost keeps how the original was priced -- a shipper reposting a
    // fixed-price load did not silently ask for bids.
    pricing_model: load.pricing_model,
    fixed_price_inr: load.fixed_price_inr ?? "",
    pickup_address: load.pickup_address,
    pickup_contact_name: load.pickup_contact_name ?? "",
    pickup_contact_phone: load.pickup_contact_phone ?? "",
    pickup_lat: load.pickup_lat,
    pickup_lng: load.pickup_lng,
    pickup_district: load.pickup_district,
    // The shipper has to pick a NEW pickup window — the old one is
    // almost certainly in the past. Leave blank to force them to fill.
    pickup_window_start: "",
    pickup_window_end: "",
    drop_address: load.drop_address,
    drop_contact_name: load.drop_contact_name ?? "",
    drop_contact_phone: load.drop_contact_phone ?? "",
    drop_lat: load.drop_lat,
    drop_lng: load.drop_lng,
    drop_district: load.drop_district,
    material_category: load.material_category,
    material_description: load.material_description ?? "",
    weight_kg: String(load.weight_kg),
    cargo_length_ft: load.cargo_length_ft == null ? "" : String(load.cargo_length_ft),
    cargo_width_ft: load.cargo_width_ft == null ? "" : String(load.cargo_width_ft),
    cargo_height_ft: load.cargo_height_ft == null ? "" : String(load.cargo_height_ft),
    required_vehicle_type:
      (load.required_vehicle_type as VehicleType) ?? defaultVehicle,
    required_payload_kg: String(load.required_payload_kg),
    hamali_required: (load.hamali_required as HamaliRequired) ?? "self_arrange",
    budget_min: load.budget_min,
    budget_max: load.budget_max,
    bid_window_hours: String(load.bid_window_hours ?? 6),
  };
}

export function PostLoadPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const repostSource = (location.state as { repost?: Load } | null)?.repost ?? null;
  const defaultVehicle = VEHICLE_TYPES[1]!;
  const [form, setForm] = useState<FormState>(
    repostSource !== null
      ? fromRepost(repostSource, defaultVehicle.value)
      : {
    pickup_address: "",
    pickup_contact_name: "",
    pickup_contact_phone: "",
    pickup_lat: "",
    pickup_lng: "",
    pickup_district: "",
    pickup_window_start: "",
    pickup_window_end: "",
    drop_address: "",
    drop_contact_name: "",
    drop_contact_phone: "",
    drop_lat: "",
    drop_lng: "",
    drop_district: "",
    material_category: MATERIAL_FALLBACK[0]!.code,
    material_description: "",
    weight_kg: "",
    cargo_length_ft: "",
    cargo_width_ft: "",
    cargo_height_ft: "",
    required_vehicle_type: defaultVehicle.value,
    required_payload_kg: String(defaultVehicle.payload),
    hamali_required: "self_arrange",
    pricing_model: "bidding",
    fixed_price_inr: "",
    budget_min: "",
    budget_max: "",
    bid_window_hours: "6",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [extraDrops, setExtraDrops] = useState<ExtraDropDraft[]>([]);

  // p9. The catalog decides which models a shipper may post under; the
  // server refuses the rest with PRICING_MODEL_DISABLED. Falling back to
  // bidding-only on failure keeps the form usable -- bidding cannot be
  // disabled (AD-07 forbids it), so that fallback can never offer
  // something the server would reject.
  // C-24. Same shape as the pricing-model fetch below: catalog-driven, with
  // a fallback that keeps the form usable rather than blocking a post on a
  // masterdata request.
  const [goodsTypes, setGoodsTypes] = useState<ReferenceItem[]>(
    MATERIAL_FALLBACK,
  );

  useEffect(() => {
    if (accessToken === null) {return;}
    const ac = new AbortController();
    referenceDataApi
      .listGoodsTypes(accessToken, ac.signal)
      .then((rows) => {
        if (rows.length === 0) {return;}
        setGoodsTypes(rows);
        // Only while the form still holds the fallback -- a shipper who has
        // already picked something must not have it changed underneath them.
        setForm((f) =>
          f.material_category === MATERIAL_FALLBACK[0]!.code
            ? { ...f, material_category: rows[0]!.code }
            : f,
        );
      })
      .catch(() => { /* keep the fallback */ });
    return () => { ac.abort(); };
  }, [accessToken]);

  const [enabledModels, setEnabledModels] = useState<PublicPricingModel[]>([
    {
      code: "bidding",
      display_name: "Drivers bid",
      description: null,
      is_recommended: true,
    },
  ]);
  const [modelsLoading, setModelsLoading] = useState(true);
  const isBidding = form.pricing_model === "bidding";

  useEffect(() => {
    if (accessToken === null) {setModelsLoading(false); return;}
    const ac = new AbortController();
    pricingModelsApi
      .listEnabled(accessToken, ac.signal)
      .then((models) => {
        if (models.length === 0) {return;}
        setEnabledModels(models);
        // p9 "show recommended model": preselect it, so a shipper who
        // does not know which pricing model to use is not left guessing.
        // Only on first load -- overriding a choice the shipper has
        // already made would be worse than not recommending at all.
        const rec = models.find((m) => m.is_recommended);
        if (rec !== undefined) {
          setForm((f) =>
            f.pricing_model === "bidding"
              ? { ...f, pricing_model: rec.code }
              : f,
          );
        }
      })
      .catch(() => { /* keep the bidding-only fallback */ })
      .finally(() => { setModelsLoading(false); });
    return () => { ac.abort(); };
  }, [accessToken]);

  function update<K extends keyof FormState>(key: K, value: FormState[K]): void {
    setForm((f) => ({ ...f, [key]: value }));
  }

  // PR-07 / p126 "optimised sequence with manual override".
  //
  // Only offered with at least one extra drop -- with a single drop there
  // is nothing to reorder, and a button that always returns "already
  // optimal" trains people to ignore it.
  //
  // It reorders ALL the drops, main included, because the main drop is not
  // privileged geographically: it is just the one the form asks for first.
  // Leaving it pinned would produce a "suggestion" the optimiser did not
  // make.
  const [routeHint, setRouteHint] = useState<string | null>(null);
  const [optimising, setOptimising] = useState(false);

  async function optimiseDropOrder(): Promise<void> {
    if (accessToken === null) {return;}
    const drops = [
      {
        address: form.drop_address,
        contact_name: form.drop_contact_name,
        contact_phone: form.drop_contact_phone,
        district: form.drop_district,
        lat: form.drop_lat,
        lng: form.drop_lng,
        instructions: "",
      },
      ...extraDrops,
    ];
    if (drops.some((d) => d.lat.trim() === "" || d.lng.trim() === "")) {
      setRouteHint("Add coordinates for every drop first.");
      return;
    }

    setOptimising(true);
    setRouteHint(null);
    try {
      const res = await routeApi.preview(accessToken, [
        {
          sequence: 1,
          stop_type: "pickup",
          lat: Number.parseFloat(form.pickup_lat),
          lng: Number.parseFloat(form.pickup_lng),
        },
        ...drops.map((d, i) => ({
          sequence: i + 2,
          stop_type: "drop" as const,
          lat: Number.parseFloat(d.lat),
          lng: Number.parseFloat(d.lng),
        })),
      ]);

      if (!res.is_worth_applying) {
        // Say so rather than silently doing nothing. A button that appears
        // to no-op reads as broken.
        setRouteHint(
          `Your order is already about as short as it gets (${res.current_distance_km} km).`,
        );
        return;
      }

      // `suggested_sequence` returns OUR numbers reordered, so applying it
      // is a permutation of what the shipper typed -- no mapping needed.
      // Sequence 1 is the pickup and is dropped here; the rest are drops in
      // their new order.
      const newDropOrder = res.suggested_sequence
        .filter((seq) => seq >= 2)
        .map((seq) => drops[seq - 2]!);

      const [first, ...rest] = newDropOrder;
      if (first === undefined) {return;}
      setForm((f) => ({
        ...f,
        drop_address: first.address,
        drop_contact_name: first.contact_name,
        drop_contact_phone: first.contact_phone,
        drop_district: first.district,
        drop_lat: first.lat,
        drop_lng: first.lng,
      }));
      setExtraDrops(rest);
      setRouteHint(
        res.fixes_infeasible_order
          // Not a distance win -- this order may be LONGER. Saying "km
          // shorter" here would be false, and saying nothing would leave
          // the shipper wondering why their drops moved.
          ? "Reordered so every pickup comes before its drops."
          : `Reordered — about ${res.saving_km} km shorter (${res.saving_pct}%).`,
      );
    } catch {
      // Advisory feature: a failure must not block posting the load.
      setRouteHint("Could not check the route just now.");
    } finally {
      setOptimising(false);
    }
  }

  function updateExtraDrop(
    idx: number,
    key: keyof ExtraDropDraft,
    value: string,
  ): void {
    setExtraDrops((cur) =>
      cur.map((d, i) => (i === idx ? { ...d, [key]: value } : d)),
    );
  }

  function onVehicleChange(value: VehicleType): void {
    const meta = VEHICLE_TYPES.find((v) => v.value === value);
    setForm((f) => ({
      ...f,
      required_vehicle_type: value,
      required_payload_kg: meta ? String(meta.payload) : f.required_payload_kg,
    }));
  }

  function validate(): string | null {
    const requiredText: (keyof FormState)[] = [
      "pickup_address",
      "pickup_contact_name",
      "pickup_contact_phone",
      "pickup_district",
      "drop_address",
      "drop_contact_name",
      "drop_contact_phone",
      "drop_district",
    ];
    for (const k of requiredText) {
      if (form[k].trim().length === 0) {return `Enter ${k.replace(/_/g, " ")}.`;}
    }
    if (form.pickup_window_start.length === 0) {return "Pick a pickup start time.";}
    if (form.pickup_window_end.length === 0) {return "Pick a pickup end time.";}
    if (form.pickup_window_end <= form.pickup_window_start) {
      return "Pickup end must be after pickup start.";
    }
    const weight = Number.parseInt(form.weight_kg, 10);
    const payload = Number.parseInt(form.required_payload_kg, 10);
    if (!Number.isFinite(weight) || weight < 1) {return "Enter cargo weight in kg.";}
    if (!Number.isFinite(payload) || payload < 500 || payload > 4000) {
      return "Vehicle payload must be between 500 and 4000 kg.";
    }
    if (weight > payload) {return "Cargo weight can't exceed vehicle payload.";}
    // PR-11: the backend requires budget for `bidding` and fixed_price_inr
    // for every other model, and REFUSES the other field. Mirror that here
    // so the shipper sees the problem before the round-trip.
    if (form.pricing_model === "bidding") {
      const min = Number.parseFloat(form.budget_min);
      const max = Number.parseFloat(form.budget_max);
      if (!Number.isFinite(min) || min < 100) {return "Min budget must be ≥ ₹100.";}
      if (!Number.isFinite(max) || max < min) {return "Max budget must be ≥ min budget.";}
    } else {
      const fixed = Number.parseFloat(form.fixed_price_inr);
      if (!Number.isFinite(fixed) || fixed < 100) {
        return "Enter the price you are offering (≥ ₹100).";
      }
    }
    const pLat = Number.parseFloat(form.pickup_lat);
    const pLng = Number.parseFloat(form.pickup_lng);
    const dLat = Number.parseFloat(form.drop_lat);
    const dLng = Number.parseFloat(form.drop_lng);
    if (!Number.isFinite(pLat) || pLat < -90 || pLat > 90) {return "Invalid pickup latitude.";}
    if (!Number.isFinite(pLng) || pLng < -180 || pLng > 180) {return "Invalid pickup longitude.";}
    if (!Number.isFinite(dLat) || dLat < -90 || dLat > 90) {return "Invalid drop latitude.";}
    if (!Number.isFinite(dLng) || dLng < -180 || dLng > 180) {return "Invalid drop longitude.";}
    // Validate every extra drop the shipper added.
    for (const [i, d] of extraDrops.entries()) {
      const label = `extra drop #${i + 2}`;
      const requiredText: (keyof ExtraDropDraft)[] = [
        "address", "contact_name", "contact_phone", "district",
      ];
      for (const k of requiredText) {
        if (d[k].trim().length === 0) {
          return `Fill ${k.replace(/_/g, " ")} on ${label}.`;
        }
      }
      const eLat = Number.parseFloat(d.lat);
      const eLng = Number.parseFloat(d.lng);
      if (!Number.isFinite(eLat) || eLat < -90 || eLat > 90) {
        return `Invalid latitude on ${label}.`;
      }
      if (!Number.isFinite(eLng) || eLng < -180 || eLng > 180) {
        return `Invalid longitude on ${label}.`;
      }
    }
    return null;
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const v = validate();
    if (v !== null) {
      setError(v);
      return;
    }
    if (accessToken === null) {
      setError("Session expired. Please sign in again.");
      return;
    }
    setSubmitting(true);
    setError(null);
    const trimmedDesc = form.material_description.trim();
    // If the shipper added extra drops, build the multi-stop `stops`
    // array. Backend still wants the legacy pickup_/drop_ mirror block
    // alongside, so we keep both — first-pickup + last-drop on the
    // mirror, full sequence in stops.
    let stops: LoadStopPostPayload[] | undefined;
    if (extraDrops.length > 0) {
      const pickup: LoadStopPostPayload = {
        sequence: 1,
        stop_type: "pickup",
        address: form.pickup_address.trim(),
        contact_name: form.pickup_contact_name.trim(),
        contact_phone: form.pickup_contact_phone.trim(),
        lat: Number.parseFloat(form.pickup_lat),
        lng: Number.parseFloat(form.pickup_lng),
        district: form.pickup_district.trim(),
        window_start: toIsoOrEmpty(form.pickup_window_start),
        window_end: toIsoOrEmpty(form.pickup_window_end),
      };
      const mainDrop: LoadStopPostPayload = {
        sequence: 2,
        stop_type: "drop",
        address: form.drop_address.trim(),
        contact_name: form.drop_contact_name.trim(),
        contact_phone: form.drop_contact_phone.trim(),
        lat: Number.parseFloat(form.drop_lat),
        lng: Number.parseFloat(form.drop_lng),
        district: form.drop_district.trim(),
      };
      const extras: LoadStopPostPayload[] = extraDrops.map((d, i) => ({
        sequence: 3 + i,
        stop_type: "drop",
        address: d.address.trim(),
        contact_name: d.contact_name.trim(),
        contact_phone: d.contact_phone.trim(),
        lat: Number.parseFloat(d.lat),
        lng: Number.parseFloat(d.lng),
        ...(d.instructions.trim().length > 0
          ? { instructions: d.instructions.trim() }
          : {}),
        district: d.district.trim(),
      }));
      stops = [pickup, mainDrop, ...extras];
    }
    const payload: LoadPostPayload = {
      pickup_address: form.pickup_address.trim(),
      pickup_contact_name: form.pickup_contact_name.trim(),
      pickup_contact_phone: form.pickup_contact_phone.trim(),
      pickup_lat: Number.parseFloat(form.pickup_lat),
      pickup_lng: Number.parseFloat(form.pickup_lng),
      pickup_district: form.pickup_district.trim(),
      pickup_window_start: toIsoOrEmpty(form.pickup_window_start),
      pickup_window_end: toIsoOrEmpty(form.pickup_window_end),
      drop_address: form.drop_address.trim(),
      drop_contact_name: form.drop_contact_name.trim(),
      drop_contact_phone: form.drop_contact_phone.trim(),
      drop_lat: Number.parseFloat(form.drop_lat),
      drop_lng: Number.parseFloat(form.drop_lng),
      drop_district: form.drop_district.trim(),
      material_category: form.material_category,
      weight_kg: Number.parseInt(form.weight_kg, 10),
      // Blank means "not stated", which is NOT the same as zero -- the
      // backend CHECK rejects a zero dimension precisely so it cannot be
      // read later as "fits anywhere".
      cargo_length_ft: form.cargo_length_ft ? Number(form.cargo_length_ft) : null,
      cargo_width_ft: form.cargo_width_ft ? Number(form.cargo_width_ft) : null,
      cargo_height_ft: form.cargo_height_ft ? Number(form.cargo_height_ft) : null,
      required_vehicle_type: form.required_vehicle_type,
      required_payload_kg: Number.parseInt(form.required_payload_kg, 10),
      hamali_required: form.hamali_required,
      pricing_model: form.pricing_model,
      ...(form.pricing_model === "bidding"
        ? {
            budget_min: Number.parseFloat(form.budget_min),
            budget_max: Number.parseFloat(form.budget_max),
          }
        : { fixed_price_inr: Number.parseFloat(form.fixed_price_inr) }),
      bid_window_hours: Number.parseInt(form.bid_window_hours, 10),
      ...(trimmedDesc.length > 0 ? { material_description: trimmedDesc } : {}),
      ...(stops !== undefined ? { stops } : {}),
    };
    try {
      await loadsApi.post(accessToken, payload);
      navigate("/", { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.problem.detail
          : "Couldn't post the load. Try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Post a load"
        description="Drivers in your area will start bidding within minutes."
        actions={
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4" />
              My loads
            </Button>
          </Link>
        }
      />

      <div className="mx-auto max-w-3xl">
        <form onSubmit={onSubmit} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Pickup</CardTitle>
              <CardDescription>Where the driver collects the cargo.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2 space-y-2">
                <Label htmlFor="p_address">Address</Label>
                <Input
                  id="p_address"
                  value={form.pickup_address}
                  onChange={(e) => { update("pickup_address", e.target.value); }}
                  required
                  disabled={submitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="p_name">Contact name</Label>
                <Input
                  id="p_name"
                  value={form.pickup_contact_name}
                  onChange={(e) => { update("pickup_contact_name", e.target.value); }}
                  required
                  disabled={submitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="p_phone">Contact phone</Label>
                <Input
                  id="p_phone"
                  type="tel"
                  value={form.pickup_contact_phone}
                  onChange={(e) => { update("pickup_contact_phone", e.target.value); }}
                  required
                  disabled={submitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="p_district">District</Label>
                <Input
                  id="p_district"
                  value={form.pickup_district}
                  onChange={(e) => { update("pickup_district", e.target.value); }}
                  required
                  disabled={submitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="p_lat">Latitude</Label>
                <Input
                  id="p_lat"
                  inputMode="decimal"
                  placeholder="13.0827"
                  value={form.pickup_lat}
                  onChange={(e) => { update("pickup_lat", e.target.value); }}
                  required
                  disabled={submitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="p_lng">Longitude</Label>
                <Input
                  id="p_lng"
                  inputMode="decimal"
                  placeholder="80.2707"
                  value={form.pickup_lng}
                  onChange={(e) => { update("pickup_lng", e.target.value); }}
                  required
                  disabled={submitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="p_start">Window starts</Label>
                <Input
                  id="p_start"
                  type="datetime-local"
                  value={form.pickup_window_start}
                  onChange={(e) => { update("pickup_window_start", e.target.value); }}
                  required
                  disabled={submitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="p_end">Window ends</Label>
                <Input
                  id="p_end"
                  type="datetime-local"
                  value={form.pickup_window_end}
                  onChange={(e) => { update("pickup_window_end", e.target.value); }}
                  required
                  disabled={submitting}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Drop</CardTitle>
              <CardDescription>Final destination for the cargo.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2 space-y-2">
                <Label htmlFor="d_address">Address</Label>
                <Input
                  id="d_address"
                  value={form.drop_address}
                  onChange={(e) => { update("drop_address", e.target.value); }}
                  required
                  disabled={submitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="d_name">Contact name</Label>
                <Input
                  id="d_name"
                  value={form.drop_contact_name}
                  onChange={(e) => { update("drop_contact_name", e.target.value); }}
                  required
                  disabled={submitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="d_phone">Contact phone</Label>
                <Input
                  id="d_phone"
                  type="tel"
                  value={form.drop_contact_phone}
                  onChange={(e) => { update("drop_contact_phone", e.target.value); }}
                  required
                  disabled={submitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="d_district">District</Label>
                <Input
                  id="d_district"
                  value={form.drop_district}
                  onChange={(e) => { update("drop_district", e.target.value); }}
                  required
                  disabled={submitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="d_lat">Latitude</Label>
                <Input
                  id="d_lat"
                  inputMode="decimal"
                  placeholder="11.0168"
                  value={form.drop_lat}
                  onChange={(e) => { update("drop_lat", e.target.value); }}
                  required
                  disabled={submitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="d_lng">Longitude</Label>
                <Input
                  id="d_lng"
                  inputMode="decimal"
                  placeholder="76.9558"
                  value={form.drop_lng}
                  onChange={(e) => { update("drop_lng", e.target.value); }}
                  required
                  disabled={submitting}
                />
              </div>
            </CardContent>
          </Card>

          {/* Multi-stop: extra drops. Optional — empty by default, the
              shipper can click "Add another drop" to chain stops on
              the same trip (Spec 8 / Multi-stop Plan A). */}
          {extraDrops.map((d, idx) => (
            <Card key={idx}>
              <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
                <div>
                  <CardTitle className="text-base">
                    Extra drop #{idx + 2}
                  </CardTitle>
                  <CardDescription>
                    Driver visits this stop after the main drop in
                    sequence order.
                  </CardDescription>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setExtraDrops((cur) => cur.filter((_, i) => i !== idx));
                  }}
                  disabled={submitting}
                  aria-label="Remove this stop"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2 space-y-2">
                  <Label htmlFor={`ed_addr_${idx}`}>Address</Label>
                  <Input
                    id={`ed_addr_${idx}`}
                    value={d.address}
                    onChange={(e) => { updateExtraDrop(idx, "address", e.target.value); }}
                    required
                    disabled={submitting}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`ed_name_${idx}`}>Contact name</Label>
                  <Input
                    id={`ed_name_${idx}`}
                    value={d.contact_name}
                    onChange={(e) => { updateExtraDrop(idx, "contact_name", e.target.value); }}
                    required
                    disabled={submitting}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`ed_phone_${idx}`}>Contact phone</Label>
                  <Input
                    id={`ed_phone_${idx}`}
                    type="tel"
                    value={d.contact_phone}
                    onChange={(e) => { updateExtraDrop(idx, "contact_phone", e.target.value); }}
                    required
                    disabled={submitting}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`ed_dist_${idx}`}>District</Label>
                  <Input
                    id={`ed_dist_${idx}`}
                    value={d.district}
                    onChange={(e) => { updateExtraDrop(idx, "district", e.target.value); }}
                    required
                    disabled={submitting}
                  />
                </div>
                {/* PR-07. Optional and last: most stops need nothing said,
                    and putting it above the required fields would imply
                    otherwise. */}
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor={`ed_instr_${idx}`}>
                    Instructions for this drop (optional)
                  </Label>
                  <Input
                    id={`ed_instr_${idx}`}
                    value={d.instructions}
                    maxLength={300}
                    placeholder="Ask for Mr Rao at the second gate"
                    onChange={(e) => { updateExtraDrop(idx, "instructions", e.target.value); }}
                    disabled={submitting}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`ed_lat_${idx}`}>Latitude</Label>
                  <Input
                    id={`ed_lat_${idx}`}
                    inputMode="decimal"
                    value={d.lat}
                    onChange={(e) => { updateExtraDrop(idx, "lat", e.target.value); }}
                    required
                    disabled={submitting}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`ed_lng_${idx}`}>Longitude</Label>
                  <Input
                    id={`ed_lng_${idx}`}
                    inputMode="decimal"
                    value={d.lng}
                    onChange={(e) => { updateExtraDrop(idx, "lng", e.target.value); }}
                    required
                    disabled={submitting}
                  />
                </div>
              </CardContent>
            </Card>
          ))}

          <Button
            type="button"
            variant="outline"
            disabled={submitting || extraDrops.length >= 18}
            onClick={() => {
              setExtraDrops((cur) => [...cur, emptyExtraDrop()]);
            }}
          >
            <Plus className="mr-1 h-4 w-4" />
            {extraDrops.length === 0
              ? "Add another drop"
              : `Add drop #${extraDrops.length + 3}`}
          </Button>

          {/* PR-07. Only with an extra drop: with one drop there is nothing
              to reorder, and a button that always answers "already optimal"
              teaches people to ignore it. */}
          {extraDrops.length > 0 ? (
            <div className="space-y-1">
              <Button
                type="button"
                variant="outline"
                disabled={submitting || optimising}
                onClick={() => { void optimiseDropOrder(); }}
              >
                {optimising ? "Checking…" : "Optimise drop order"}
              </Button>
              {routeHint !== null ? (
                <p className="text-xs text-muted-foreground">{routeHint}</p>
              ) : null}
            </div>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Cargo & vehicle</CardTitle>
              <CardDescription>What's being moved and on what vehicle.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="material">Material category</Label>
                <select
                  id="material"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={form.material_category}
                  onChange={(e) => { update("material_category", e.target.value); }}
                  disabled={submitting}
                >
                  {goodsTypes.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="weight">Weight (kg)</Label>
                <Input
                  id="weight"
                  inputMode="numeric"
                  value={form.weight_kg}
                  onChange={(e) => { update("weight_kg", e.target.value.replace(/\D/g, "")); }}
                  required
                  disabled={submitting}
                />
              </div>
              {/* C-23: dimensions. Optional -- weight binds for most loads,
                  but a driver with an 8 ft bay needs to know about a 12 ft
                  pipe BEFORE driving to the pickup. */}
              <div className="sm:col-span-2 space-y-2">
                <Label>Cargo size in feet (optional)</Label>
                <div className="grid grid-cols-3 gap-2">
                  {([
                    ["cargo_length_ft", "Length"],
                    ["cargo_width_ft", "Width"],
                    ["cargo_height_ft", "Height"],
                  ] as const).map(([field, label]) => (
                    <Input
                      key={field}
                      aria-label={`${label} in feet`}
                      placeholder={label}
                      inputMode="decimal"
                      value={form[field]}
                      onChange={(e) => {
                        // Digits and at most one dot. Feet are commonly
                        // fractional ("8.5"), so this cannot be integer-only
                        // the way weight is.
                        const cleaned = e.target.value
                          .replace(/[^\d.]/g, "")
                          .replace(/(\..*)\./g, "$1");
                        update(field, cleaned);
                      }}
                      disabled={submitting}
                    />
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  Helps drivers check the load fits before they bid.
                </p>
              </div>
              <div className="sm:col-span-2 space-y-2">
                <Label htmlFor="desc">Description (optional)</Label>
                <Textarea
                  id="desc"
                  rows={2}
                  value={form.material_description}
                  onChange={(e) => { update("material_description", e.target.value); }}
                  disabled={submitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="veh">Vehicle type</Label>
                <select
                  id="veh"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={form.required_vehicle_type}
                  onChange={(e) => { onVehicleChange(e.target.value as VehicleType); }}
                  disabled={submitting}
                >
                  {VEHICLE_TYPES.map((v) => (
                    <option key={v.value} value={v.value}>
                      {v.label} ({v.payload} kg)
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="payload">Required payload (kg)</Label>
                <Input
                  id="payload"
                  inputMode="numeric"
                  value={form.required_payload_kg}
                  onChange={(e) => { update("required_payload_kg", e.target.value.replace(/\D/g, "")); }}
                  required
                  disabled={submitting}
                />
              </div>
              <div className="sm:col-span-2 space-y-2">
                <Label htmlFor="hamali">Loading/unloading</Label>
                <select
                  id="hamali"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={form.hamali_required}
                  onChange={(e) => { update("hamali_required", e.target.value as HamaliRequired); }}
                  disabled={submitting}
                >
                  {HAMALI_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Pricing</CardTitle>
              <CardDescription>
                {isBidding
                  ? "Drivers will see this range and bid within it."
                  : "Drivers accept this price as posted — no bidding round."}
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-3">
              {/*
                p9: "Customer sees applicable model while posting load."
                Only enabled models are offered — the catalog is the source
                of truth (AD-07), and the server refuses a disabled code with
                PRICING_MODEL_DISABLED, so offering one here would just
                produce a round-trip error.
              */}
              <div className="space-y-2 sm:col-span-3">
                <Label htmlFor="pricing_model">How are you pricing this?</Label>
                <select
                  id="pricing_model"
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={form.pricing_model}
                  onChange={(e) => {
                    update("pricing_model", e.target.value as PricingModelCode);
                  }}
                  disabled={submitting || modelsLoading}
                >
                  {enabledModels.map((m) => (
                    <option key={m.code} value={m.code}>
                      {m.is_recommended
                        ? `${m.display_name} (recommended)`
                        : m.display_name}
                    </option>
                  ))}
                </select>
              </div>

              {isBidding ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="bmin">Min budget (₹)</Label>
                    <Input
                      id="bmin"
                      inputMode="numeric"
                      value={form.budget_min}
                      onChange={(e) => { update("budget_min", e.target.value.replace(/[^\d.]/g, "")); }}
                      required
                      disabled={submitting}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bmax">Max budget (₹)</Label>
                    <Input
                      id="bmax"
                      inputMode="numeric"
                      value={form.budget_max}
                      onChange={(e) => { update("budget_max", e.target.value.replace(/[^\d.]/g, "")); }}
                      required
                      disabled={submitting}
                    />
                  </div>
                </>
              ) : (
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="fixed_price">Price you are offering (₹)</Label>
                  <Input
                    id="fixed_price"
                    inputMode="numeric"
                    value={form.fixed_price_inr}
                    onChange={(e) => { update("fixed_price_inr", e.target.value.replace(/[^\d.]/g, "")); }}
                    required
                    disabled={submitting}
                  />
                  <p className="text-xs text-muted-foreground">
                    The first driver to accept takes the load at this price.
                  </p>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="bid_hours">Bid window (hours)</Label>
                <Input
                  id="bid_hours"
                  inputMode="numeric"
                  value={form.bid_window_hours}
                  onChange={(e) => { update("bid_window_hours", e.target.value.replace(/\D/g, "")); }}
                  required
                  disabled={submitting}
                />
              </div>
            </CardContent>
          </Card>

          {error !== null ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          <div className="flex justify-end">
            <Button type="submit" disabled={submitting} size="lg">
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Posting…
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  Post load
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
